import { useState, useEffect, lazy, Suspense, Component, ReactNode } from 'react';
import { Routes, Route, Navigate, useLocation, Link, useNavigate } from 'react-router-dom';
import { useAuth } from './store/authStore';
import { TemplateStudio } from './pages/TemplateStudio';
import { TemplateList } from './pages/TemplateList';
import { ClauseLibrary } from './pages/ClauseLibrary';
import { Dashboard } from './pages/Dashboard';
import { ClientList } from './pages/ClientList';
import { MatterList } from './pages/MatterList';
import { DocumentList } from './pages/DocumentList';
import { Reports } from './pages/Reports';
import { Settings } from './pages/Settings';
import { HelpDocs } from './pages/HelpDocs';
import Login from './pages/Login';

// Lazy-loaded Lawity Pages for code splitting
const RainmakerPage = lazy(() => import('./pages/RainmakerPage').then(m => ({ default: m.RainmakerPage })));
const ConnectorsPage = lazy(() => import('./pages/ConnectorsPage').then(m => ({ default: m.ConnectorsPage })));
const AIAgentsPage = lazy(() => import('./pages/AIAgentsPage').then(m => ({ default: m.AIAgentsPage })));
const DealEnginePage = lazy(() => import('./pages/DealEnginePage').then(m => ({ default: m.DealEnginePage })));
const BriefingPage = lazy(() => import('./pages/BriefingPage').then(m => ({ default: m.BriefingPage })));
const DecisionQueuePage = lazy(() => import('./pages/DecisionQueuePage').then(m => ({ default: m.DecisionQueuePage })));

// Growth Engine Pages
const ReciprocityDashboard = lazy(() => import('./pages/ReciprocityDashboard').then(m => ({ default: m.ReciprocityDashboard })));
const InstaBragGenerator = lazy(() => import('./pages/InstaBragGenerator').then(m => ({ default: m.InstaBragGenerator })));
const WatchdogDashboard = lazy(() => import('./pages/WatchdogDashboard').then(m => ({ default: m.WatchdogDashboard })));
const NetworkBrokerDashboard = lazy(() => import('./pages/NetworkBrokerDashboard').then(m => ({ default: m.NetworkBrokerDashboard })));

// Billing & Practice Pages
const BillingPage = lazy(() => import('./pages/BillingPage').then(m => ({ default: m.BillingPage })));

// Real Estate / TITAN Pages
const TitanClosingPage = lazy(() => import('./pages/TitanClosingPage').then(m => ({ default: m.TitanClosingPage })));

// Fiduciary & Trust Pages
const FiduciaryDashboard = lazy(() => import('./pages/FiduciaryDashboard').then(m => ({ default: m.FiduciaryDashboard })));

// Intake & Client Onboarding
const IntakePortal = lazy(() => import('./pages/IntakePortal').then(m => ({ default: m.IntakePortal })));

// Negotiation & Contract Pages
const NegotiatorDashboard = lazy(() => import('./pages/NegotiatorDashboard').then(m => ({ default: m.NegotiatorDashboard })));
const ContractVerifier = lazy(() => import('./pages/ContractVerifier').then(m => ({ default: m.ContractVerifier })));

// Client Engagement & Wellness
const LegalWellnessPortal = lazy(() => import('./pages/LegalWellnessPortal').then(m => ({ default: m.LegalWellnessPortal })));

// Partner & Referral Pages
const PartnerPortal = lazy(() => import('./pages/PartnerPortal').then(m => ({ default: m.PartnerPortal })));

// Due Diligence & Investigation
const DueDiligencePage = lazy(() => import('./pages/DueDiligencePage').then(m => ({ default: m.DueDiligencePage })));

// Crypto & Digital Assets
const DigitalVault = lazy(() => import('./pages/DigitalVault').then(m => ({ default: m.DigitalVault })));

// Real Estate / TITAN Extended Pages
const SettlementEngine = lazy(() => import('./pages/SettlementEngine').then(m => ({ default: m.SettlementEngine })));
const UnderwriterBridge = lazy(() => import('./pages/UnderwriterBridge').then(m => ({ default: m.UnderwriterBridge })));
const LivingTitle = lazy(() => import('./pages/LivingTitle').then(m => ({ default: m.LivingTitle })));
const ERecordingPage = lazy(() => import('./pages/ERecordingPage').then(m => ({ default: m.ERecordingPage })));
const RONPage = lazy(() => import('./pages/RONPage').then(m => ({ default: m.RONPage })));
const ProfitEngine = lazy(() => import('./pages/ProfitEngine').then(m => ({ default: m.ProfitEngine })));
const TitleExamPage = lazy(() => import('./pages/TitleExamPage').then(m => ({ default: m.TitleExamPage })));
const AssetScoutPage = lazy(() => import('./pages/AssetScoutPage').then(m => ({ default: m.AssetScoutPage })));

// AI & Litigation Pages
const AmbientBillingPage = lazy(() => import('./pages/AmbientBillingPage').then(m => ({ default: m.AmbientBillingPage })));
const MootCourtPage = lazy(() => import('./pages/MootCourtPage').then(m => ({ default: m.MootCourtPage })));
const DeepTheoryPage = lazy(() => import('./pages/DeepTheoryPage').then(m => ({ default: m.DeepTheoryPage })));
const ResearchPage = lazy(() => import('./pages/ResearchPage').then(m => ({ default: m.ResearchPage })));
const CondoCommandPage = lazy(() => import('./pages/CondoCommandPage').then(m => ({ default: m.CondoCommandPage })));
const LegacyVaultPage = lazy(() => import('./pages/LegacyVaultPage').then(m => ({ default: m.LegacyVaultPage })));
const SunbizScoutPage = lazy(() => import('./pages/SunbizScoutPage').then(m => ({ default: m.SunbizScoutPage })));

// M&A / Deal Pages
const DealRoomPage = lazy(() => import('./pages/DealRoomPage').then(m => ({ default: m.DealRoomPage })));
const DealBenchmarkPage = lazy(() => import('./pages/DealBenchmarkPage').then(m => ({ default: m.DealBenchmarkPage })));
const DealScannerPage = lazy(() => import('./pages/DealScannerPage').then(m => ({ default: m.DealScannerPage })));
const SynergyScoutPage = lazy(() => import('./pages/SynergyScoutPage').then(m => ({ default: m.SynergyScoutPage })));

// Document & Contract Pages
const DocumentAssemblyPage = lazy(() => import('./pages/DocumentAssemblyPage').then(m => ({ default: m.DocumentAssemblyPage })));
const NegotiationTimelinePage = lazy(() => import('./pages/NegotiationTimelinePage').then(m => ({ default: m.NegotiationTimelinePage })));
const ModelDocumentAnalyzerPage = lazy(() => import('./pages/ModelDocumentAnalyzerPage').then(m => ({ default: m.ModelDocumentAnalyzerPage })));

// Corporate & Trust Pages
const CorporateMirrorPage = lazy(() => import('./pages/CorporateMirrorPage').then(m => ({ default: m.CorporateMirrorPage })));
const FortKnoxAuditPage = lazy(() => import('./pages/FortKnoxAuditPage').then(m => ({ default: m.FortKnoxAuditPage })));

// Foundation ERP Pages
const FoundationDashboard = lazy(() => import('./pages/foundation/FoundationDashboard').then(m => ({ default: m.FoundationDashboard })));
const FoundationPlaceholder = lazy(() => import('./pages/foundation/FoundationPlaceholder').then(m => ({ default: m.FoundationPlaceholder })));

// Entity Intake Pages
const EntityWizard = lazy(() => import('./pages/intake/EntityWizard').then(m => ({ default: m.EntityWizard })));

import { NotificationProvider, NotificationBell, ToastContainer } from './components/Notifications';
import { EntityProvider } from './context/EntityContext';
import { DashboardRouter } from './pages/dashboard/DashboardRouter';
import { EntitySelector } from './components/entity/EntitySelector';
import { CommandPalette, useCommandPalette } from './components/CommandPalette';
import { AIAssistant, useAIAssistant } from './components/AIAssistant';
import { AITemplateGenerator } from './components/AITemplateGenerator';
import { AIDocumentAnalyzer } from './components/AIDocumentAnalyzer';
import { AISmartFill } from './components/AISmartFill';
import { AIClauseSuggestions } from './components/AIClauseSuggestions';
import { AIFloatingButton } from './components/AIFloatingButton';
import { KeyboardShortcuts } from './components/KeyboardShortcuts';
import { OnboardingTour, useOnboarding } from './components/OnboardingTour';
import {
  Loader2, LayoutDashboard, FileText, Users, Briefcase,
  FolderOpen, Settings as SettingsIcon, LogOut, Scale, ChevronRight,
  Files, HelpCircle, BarChart2, Keyboard, Target, Plug, Brain,
  Headphones, CheckSquare, Sparkles, Shield, Network, TrendingUp,
  DollarSign, Building2, Landmark, UserPlus, MessageSquare, FileCheck,
  Heart, Users2, Search, Lock, Calculator, FileSignature, Eye, Radio,
  Video, PiggyBank, Mic, Swords, Lightbulb, Home, BarChart, Scan,
  Link2, FileStack, GitBranch, BookOpen, Building, ShieldCheck, Layers
} from 'lucide-react';

// Handshake/Partner icon alias
const HandshakeIcon = Users2;

// Handshake icon doesn't exist in this version, using Briefcase as fallback
const Handshake = Briefcase;

// Loading fallback for lazy-loaded pages
function PageLoader() {
  return (
    <div className="h-full flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-8 h-8 text-accent-500 animate-spin mx-auto mb-3" />
        <p className="text-primary-400 text-sm">Loading...</p>
      </div>
    </div>
  );
}

// Error Boundary to catch page errors
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Page Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="h-full flex items-center justify-center p-8">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-red-400 text-2xl">!</span>
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">Something went wrong</h2>
            <p className="text-primary-400 mb-4">{this.state.error?.message || 'An unexpected error occurred'}</p>
            <button
              onClick={() => {
                this.setState({ hasError: false, error: null });
                window.location.reload();
              }}
              className="px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-500 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Protected Route Component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, initialized } = useAuth();
  const location = useLocation();

  if (!initialized) {
    return (
      <div className="h-screen flex items-center justify-center bg-gradient-to-br from-primary-900 via-[#1a1f2e] to-primary-800">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-accent-500 animate-spin mx-auto mb-4" />
          <p className="text-primary-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

// Public Route (redirects to app if already logged in)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, initialized } = useAuth();

  if (!initialized) {
    return (
      <div className="h-screen flex items-center justify-center bg-gradient-to-br from-primary-900 via-[#1a1f2e] to-primary-800">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-accent-500 animate-spin mx-auto mb-4" />
          <p className="text-primary-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

// Dashboard Layout with sidebar navigation and AI features
function DashboardLayout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const commandPalette = useCommandPalette();
  const aiAssistant = useAIAssistant();
  const { showOnboarding, setShowOnboarding } = useOnboarding();

  // Modal States
  const [showTemplateGenerator, setShowTemplateGenerator] = useState(false);
  const [showDocumentAnalyzer, setShowDocumentAnalyzer] = useState(false);
  const [showSmartFill, setShowSmartFill] = useState(false);
  const [showClauseSuggestions, setShowClauseSuggestions] = useState(false);
  const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState(false);

  // Global keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Command palette is handled by useCommandPalette hook

      // Keyboard shortcuts modal: Cmd+?
      if ((e.metaKey || e.ctrlKey) && e.key === '/') {
        e.preventDefault();
        setShowKeyboardShortcuts(prev => !prev);
      }

      // AI Assistant: Cmd+Shift+A (but not conflicting with analyze)
      if ((e.metaKey || e.ctrlKey) && e.key === '\\') {
        e.preventDefault();
        aiAssistant.open({ currentPage: location.pathname });
      }

      // Quick navigation
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey) {
        switch (e.key) {
          case '1':
            e.preventDefault();
            navigate('/dashboard');
            break;
          case '2':
            e.preventDefault();
            navigate('/templates');
            break;
          case '3':
            e.preventDefault();
            navigate('/clauses');
            break;
          case '4':
            e.preventDefault();
            navigate('/clients');
            break;
          case '5':
            e.preventDefault();
            navigate('/matters');
            break;
          case '6':
            e.preventDefault();
            navigate('/documents');
            break;
          // Lawity AI & Automation
          case '7':
            e.preventDefault();
            navigate('/ai-agents');
            break;
          case '8':
            e.preventDefault();
            navigate('/rainmaker');
            break;
          case '9':
            e.preventDefault();
            navigate('/deal-engine');
            break;
          // Calm Law
          case '0':
            e.preventDefault();
            navigate('/decisions');
            break;
        }
      }

      // AI Feature shortcuts (Cmd+Shift)
      if ((e.metaKey || e.ctrlKey) && e.shiftKey) {
        switch (e.key.toLowerCase()) {
          case 'g':
            e.preventDefault();
            setShowTemplateGenerator(true);
            break;
          case 'a':
            e.preventDefault();
            setShowDocumentAnalyzer(true);
            break;
          case 'f':
            e.preventDefault();
            setShowSmartFill(true);
            break;
          case 'c':
            e.preventDefault();
            setShowClauseSuggestions(true);
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate, location.pathname, aiAssistant]);

  const navSections = [
    {
      title: 'Overview',
      items: [
        { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
        { path: '/reports', label: 'Reports', icon: BarChart2 },
      ],
    },
    {
      title: 'Documents',
      items: [
        { path: '/templates', label: 'Templates', icon: FileText },
        { path: '/clauses', label: 'Clause Library', icon: FolderOpen },
        { path: '/documents', label: 'Documents', icon: Files },
        { path: '/doc-assembly', label: 'Doc Assembly', icon: FileStack },
        { path: '/negotiation-timeline', label: 'Neg. Timeline', icon: GitBranch },
        { path: '/model-docs', label: 'Model Docs', icon: BookOpen },
      ],
    },
    {
      title: 'Practice',
      items: [
        { path: '/clients', label: 'Clients', icon: Users },
        { path: '/matters', label: 'Matters', icon: Briefcase },
        { path: '/billing', label: 'Billing', icon: DollarSign },
        { path: '/intake', label: 'Smart Intake', icon: UserPlus },
      ],
    },
    {
      title: 'Fiduciary',
      items: [
        { path: '/fiduciary', label: 'Trust Admin', icon: Landmark },
        { path: '/wellness', label: 'Legal Wellness', icon: Heart },
        { path: '/vault', label: 'Digital Vault', icon: Lock },
      ],
    },
    {
      title: 'Foundation ERP',
      items: [
        { path: '/foundation', label: 'Dashboard', icon: Building2 },
        { path: '/foundation/governance', label: 'Board & Governance', icon: Users },
        { path: '/foundation/investments', label: 'Investments', icon: TrendingUp },
        { path: '/foundation/grants', label: 'Grants', icon: Heart },
        { path: '/foundation/finance', label: 'Finance', icon: DollarSign },
        { path: '/foundation/hr', label: 'HR & Payroll', icon: Briefcase },
        { path: '/foundation/tax', label: 'Tax & Compliance', icon: Scale },
      ],
    },
    {
      title: 'Real Estate',
      items: [
        { path: '/closings', label: 'TITAN Closings', icon: Building2 },
        { path: '/settlement', label: 'Settlement Engine', icon: Calculator },
        { path: '/underwriter', label: 'Underwriter Bridge', icon: Shield },
        { path: '/living-title', label: 'Living Title', icon: Eye },
        { path: '/e-recording', label: 'E-Recording', icon: Radio },
        { path: '/ron', label: 'Remote Notary', icon: Video },
        { path: '/profit-engine', label: 'Profit Engine', icon: PiggyBank },
        { path: '/estate-plan', label: 'Estate Plan', icon: FileSignature },
        { path: '/legacy-vault', label: 'Legacy Vault', icon: Shield },
        { path: '/title-exam', label: 'Title Exam', icon: FileSignature },
        { path: '/asset-scout', label: 'Asset Scout', icon: Home },
        { path: '/condo-command', label: 'Condo Command', icon: FileText },
      ],
    },
    {
      title: 'AI & Automation',
      items: [
        { path: '/ai-agents', label: 'AI Agents', icon: Brain },
        { path: '/rainmaker', label: 'Rainmaker', icon: Target },
        { path: '/connectors', label: 'Connectors', icon: Plug },
        { path: '/deal-engine', label: 'Deal Engine', icon: Handshake },
        { path: '/negotiator', label: 'Negotiator', icon: MessageSquare },
        { path: '/verifier', label: 'Contract Verifier', icon: FileCheck },
        { path: '/ambient-billing', label: 'Ambient Billing', icon: Mic },
        { path: '/moot-court', label: 'Moot Court', icon: Swords },
        { path: '/deep-theory', label: 'Deep Theory', icon: Lightbulb },
        { path: '/research', label: 'Research & Verify', icon: Search },
      ],
    },
    {
      title: 'M&A / Deals',
      items: [
        { path: '/deal-room', label: 'Deal Room', icon: Briefcase },
        { path: '/deal-benchmark', label: 'Deal Benchmark', icon: BarChart },
        { path: '/deal-scanner', label: 'Deal Scanner', icon: Scan },
        { path: '/synergy-scout', label: 'Synergy Scout', icon: Link2 },
      ],
    },
    {
      title: 'Corporate',
      items: [
        { path: '/deal-room', label: 'Deal Room', icon: Briefcase },
        { path: '/sunbiz-scout', label: 'Sunbiz Scout', icon: Briefcase },
        { path: '/corporate-mirror', label: 'Corporate Mirror', icon: Layers },
        { path: '/fort-knox', label: 'Fort Knox Audit', icon: ShieldCheck },
      ],
    },
    {
      title: 'Due Diligence',
      items: [
        { path: '/due-diligence', label: 'Panopticon', icon: Search },
        { path: '/partners', label: 'Partner Portal', icon: HandshakeIcon },
      ],
    },
    {
      title: 'Calm Law',
      items: [
        { path: '/briefing', label: 'Briefing', icon: Headphones },
        { path: '/decisions', label: 'Decisions', icon: CheckSquare },
      ],
    },
    {
      title: 'Growth Engine',
      items: [
        { path: '/reciprocity', label: 'Reciprocity', icon: TrendingUp },
        { path: '/insta-brag', label: 'Insta-Brag', icon: Sparkles },
        { path: '/watchdog', label: 'Watchdog', icon: Shield },
        { path: '/network-broker', label: 'Network Broker', icon: Network },
      ],
    },
  ];

  // Handle generated template from AI
  const handleTemplateGenerated = (content: string, variables: any[]) => {
    console.log('Template generated:', { content, variables });
    setShowTemplateGenerator(false);
    navigate('/templates/new');
  };

  return (
    <NotificationProvider>
      <EntityProvider>
        <div className="h-screen flex bg-gradient-to-br from-primary-900 via-[#1a1f2e] to-primary-800">
          {/* Sidebar */}
          <aside className="w-64 bg-primary-900/50 border-r border-primary-700 flex flex-col">
            {/* Logo */}
            <div className="p-4 border-b border-primary-700">
              <Link to="/dashboard" className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-accent-500 flex items-center justify-center">
                  <Scale className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="font-display font-bold text-white">Lawity</h1>
                  <p className="text-xs text-primary-400">Legal Practice Management</p>
                </div>
              </Link>
            </div>

            {/* Navigation */}
            <nav className="flex-1 p-4 space-y-6 overflow-auto">
              {navSections.map((section) => (
                <div key={section.title}>
                  <p className="px-3 mb-2 text-xs font-semibold text-primary-500 uppercase tracking-wider">
                    {section.title}
                  </p>
                  <div className="space-y-1">
                    {section.items.map((item) => {
                      const isActive = location.pathname === item.path ||
                        (item.path !== '/dashboard' && item.path !== '/reports' && location.pathname.startsWith(item.path));

                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${isActive
                            ? 'bg-accent-500/20 text-accent-400'
                            : 'text-primary-300 hover:bg-white/5 hover:text-white'
                            }`}
                        >
                          <item.icon className="w-5 h-5" />
                          {item.label}
                          {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              ))}
            </nav>

            {/* Footer */}
            <div className="p-4 border-t border-primary-700 space-y-2">
              <Link
                to="/settings"
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${location.pathname === '/settings'
                  ? 'bg-accent-500/20 text-accent-400'
                  : 'text-primary-300 hover:bg-white/5 hover:text-white'
                  }`}
              >
                <SettingsIcon className="w-5 h-5" />
                Settings
              </Link>
              <Link
                to="/help"
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${location.pathname === '/help'
                  ? 'bg-accent-500/20 text-accent-400'
                  : 'text-primary-300 hover:bg-white/5 hover:text-white'
                  }`}
              >
                <HelpCircle className="w-5 h-5" />
                Help & Docs
              </Link>
              <button
                onClick={() => setShowKeyboardShortcuts(true)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-primary-300 hover:bg-white/5 hover:text-white"
              >
                <Keyboard className="w-5 h-5" />
                Shortcuts
                <kbd className="ml-auto text-xs bg-primary-700 px-1.5 py-0.5 rounded">⌘/</kbd>
              </button>
            </div>

            {/* User section */}
            <div className="p-4 border-t border-primary-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-accent-500/20 flex items-center justify-center">
                  <span className="text-accent-400 font-medium">
                    {user?.email?.[0].toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{user?.email}</p>
                  <p className="text-xs text-primary-400">Administrator</p>
                </div>
                <button
                  onClick={() => signOut()}
                  className="p-2 text-primary-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                  title="Sign out"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            </div>
          </aside>

          {/* Main content */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Top bar */}
            <header className="h-14 bg-primary-900/30 border-b border-primary-700 flex items-center justify-between px-6">
              <div className="flex items-center gap-4">
                <EntitySelector />
              </div>
              <div className="flex items-center gap-2">
                <NotificationBell />
              </div>
            </header>

            {/* Page content */}
            <main className="flex-1 overflow-auto">
              {children}
            </main>
          </div>

          {/* Toast notifications */}
          <ToastContainer />

          {/* AI Floating Button */}
          <AIFloatingButton
            onOpenChat={() => aiAssistant.open({ currentPage: location.pathname })}
            onOpenGenerator={() => setShowTemplateGenerator(true)}
            onOpenAnalyzer={() => setShowDocumentAnalyzer(true)}
            onOpenSmartFill={() => setShowSmartFill(true)}
            onOpenClauseSuggestions={() => setShowClauseSuggestions(true)}
            showAnalyzer={location.pathname.includes('/documents')}
            showSmartFill={location.pathname.includes('/templates')}
            showClauseSuggestions={location.pathname.includes('/templates') || location.pathname.includes('/clauses')}
          />

          {/* Command palette with AI features */}
          <CommandPalette
            isOpen={commandPalette.isOpen}
            onClose={commandPalette.close}
            onOpenAIAssistant={() => aiAssistant.open({ currentPage: location.pathname })}
            onOpenTemplateGenerator={() => setShowTemplateGenerator(true)}
            onOpenDocumentAnalyzer={() => setShowDocumentAnalyzer(true)}
            onOpenSmartFill={() => setShowSmartFill(true)}
            onOpenClauseSuggestions={() => setShowClauseSuggestions(true)}
          />

          {/* AI Assistant Panel */}
          <AIAssistant
            isOpen={aiAssistant.isOpen}
            onClose={aiAssistant.close}
            context={aiAssistant.context}
          />

          {/* AI Template Generator Modal */}
          <AITemplateGenerator
            isOpen={showTemplateGenerator}
            onClose={() => setShowTemplateGenerator(false)}
            onGenerated={handleTemplateGenerated}
          />

          {/* AI Document Analyzer Modal */}
          <AIDocumentAnalyzer
            isOpen={showDocumentAnalyzer}
            onClose={() => setShowDocumentAnalyzer(false)}
            documentContent="Sample document content for analysis..."
            documentType="Legal Document"
          />

          {/* AI Smart Fill Modal */}
          <AISmartFill
            isOpen={showSmartFill}
            onClose={() => setShowSmartFill(false)}
            templateVariables={[
              { name: 'grantor.name', type: 'text', required: true, label: 'Grantor Name' },
              { name: 'grantee.name', type: 'text', required: true, label: 'Grantee Name' },
              { name: 'property.address', type: 'text', required: true, label: 'Property Address' },
              { name: 'consideration', type: 'number', required: false, label: 'Consideration' },
            ]}
            onFill={(variables) => {
              console.log('Variables filled:', variables);
            }}
          />

          {/* AI Clause Suggestions Modal */}
          <AIClauseSuggestions
            isOpen={showClauseSuggestions}
            onClose={() => setShowClauseSuggestions(false)}
            documentType="Warranty Deed"
            jurisdiction="Wisconsin"
            existingClauses={['Standard Warranty', 'Legal Description']}
            onInsertClause={(content, name) => {
              console.log('Insert clause:', { name, content });
            }}
          />

          {/* Keyboard Shortcuts Modal */}
          <KeyboardShortcuts
            isOpen={showKeyboardShortcuts}
            onClose={() => setShowKeyboardShortcuts(false)}
          />

          {/* Onboarding Tour */}
          <OnboardingTour
            isOpen={showOnboarding}
            onClose={() => setShowOnboarding(false)}
            onComplete={() => setShowOnboarding(false)}
          />
        </div>
      </EntityProvider>
    </NotificationProvider>
  );
}

export default function App() {
  return (
    <div className="h-full">
      <Routes>
        {/* Public routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />

        {/* Protected routes with dashboard layout */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Navigate to="/dashboard" replace />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <DashboardRouter />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <Reports />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/templates"
          element={
            <ProtectedRoute>
              <ErrorBoundary>
                <TemplateList />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/templates/new"
          element={
            <ProtectedRoute>
              <TemplateStudio />
            </ProtectedRoute>
          }
        />
        <Route
          path="/templates/:id"
          element={
            <ProtectedRoute>
              <TemplateStudio />
            </ProtectedRoute>
          }
        />
        <Route
          path="/clauses"
          element={
            <ProtectedRoute>
              <ErrorBoundary>
                <ClauseLibrary />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/clients"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <ClientList />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/matters"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <MatterList />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/documents"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <DocumentList />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/billing"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <ErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <BillingPage />
                  </Suspense>
                </ErrorBoundary>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/foundation/*"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <ErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <Routes>
                      <Route index element={<FoundationDashboard />} />
                      <Route path="governance" element={<FoundationPlaceholder title="Board & Governance" description="Manage directors, meetings, minutes, and resolutions." />} />
                      <Route path="investments" element={<FoundationPlaceholder title="Investments" description="Track investment accounts, holdings, and PRIs." />} />
                      <Route path="grants" element={<FoundationPlaceholder title="Grantmaking" description="Manage grant programs, applications, and awards." />} />
                      <Route path="finance" element={<FoundationPlaceholder title="Finance" description="General Ledger, Budgeting, and Financial Reporting." />} />
                      <Route path="hr" element={<FoundationPlaceholder title="HR & Payroll" description="Employee management, payroll runs, and benefits." />} />
                      <Route path="tax" element={<FoundationPlaceholder title="Tax & Compliance" description="Form 990-PF generation and compliance monitoring." />} />
                    </Routes>
                  </Suspense>
                </ErrorBoundary>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        {/* Universal Entity Intake */}
        <Route
          path="/intake/new-entity"
          element={
            <ProtectedRoute>
              <ErrorBoundary>
                <Suspense fallback={<PageLoader />}>
                  <EntityWizard />
                </Suspense>
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />

        {/* Real Estate / TITAN Routes */}
        <Route
          path="/research"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <ErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <ResearchPage />
                  </Suspense>
                </ErrorBoundary>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/condo-command"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <ErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <CondoCommandPage />
                  </Suspense>
                </ErrorBoundary>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/legacy-vault"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <ErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <LegacyVaultPage />
                  </Suspense>
                </ErrorBoundary>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/sunbiz-scout"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <ErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <SunbizScoutPage />
                  </Suspense>
                </ErrorBoundary>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/closings"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <ErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <TitanClosingPage />
                  </Suspense>
                </ErrorBoundary>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <Settings />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/help"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <HelpDocs />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        {/* AI & Automation Routes (lazy-loaded) */}
        <Route
          path="/ai-agents"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <Suspense fallback={<PageLoader />}>
                  <AIAgentsPage />
                </Suspense>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/rainmaker"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <ErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <RainmakerPage />
                  </Suspense>
                </ErrorBoundary>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/connectors"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <Suspense fallback={<PageLoader />}>
                  <ConnectorsPage />
                </Suspense>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/deal-engine"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <Suspense fallback={<PageLoader />}>
                  <DealEnginePage />
                </Suspense>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/negotiator"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <ErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <NegotiatorDashboard />
                  </Suspense>
                </ErrorBoundary>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/verifier"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <ErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <ContractVerifier />
                  </Suspense>
                </ErrorBoundary>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        {/* Fiduciary & Trust Routes (lazy-loaded) */}
        <Route
          path="/fiduciary"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <ErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <FiduciaryDashboard />
                  </Suspense>
                </ErrorBoundary>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/wellness"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <ErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <LegalWellnessPortal />
                  </Suspense>
                </ErrorBoundary>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/vault"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <ErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <DigitalVault />
                  </Suspense>
                </ErrorBoundary>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/intake"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <ErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <IntakePortal />
                  </Suspense>
                </ErrorBoundary>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        {/* Due Diligence Routes (lazy-loaded) */}
        <Route
          path="/due-diligence"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <ErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <DueDiligencePage />
                  </Suspense>
                </ErrorBoundary>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/partners"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <ErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <PartnerPortal />
                  </Suspense>
                </ErrorBoundary>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        {/* Calm Law Routes (lazy-loaded) */}
        <Route
          path="/briefing"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <Suspense fallback={<PageLoader />}>
                  <BriefingPage />
                </Suspense>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/decisions"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <Suspense fallback={<PageLoader />}>
                  <DecisionQueuePage />
                </Suspense>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        {/* Growth Engine Routes (lazy-loaded) */}
        <Route
          path="/reciprocity"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <Suspense fallback={<PageLoader />}>
                  <ReciprocityDashboard />
                </Suspense>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/insta-brag"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <Suspense fallback={<PageLoader />}>
                  <InstaBragGenerator />
                </Suspense>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/watchdog"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <Suspense fallback={<PageLoader />}>
                  <WatchdogDashboard />
                </Suspense>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/network-broker"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <Suspense fallback={<PageLoader />}>
                  <NetworkBrokerDashboard />
                </Suspense>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        {/* Real Estate Extended Routes */}
        <Route
          path="/settlement"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <ErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <SettlementEngine />
                  </Suspense>
                </ErrorBoundary>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/underwriter"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <ErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <UnderwriterBridge />
                  </Suspense>
                </ErrorBoundary>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/living-title"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <ErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <LivingTitle />
                  </Suspense>
                </ErrorBoundary>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/e-recording"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <ErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <ERecordingPage />
                  </Suspense>
                </ErrorBoundary>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/ron"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <ErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <RONPage />
                  </Suspense>
                </ErrorBoundary>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/profit-engine"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <ErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <ProfitEngine />
                  </Suspense>
                </ErrorBoundary>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/title-exam"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <ErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <TitleExamPage />
                  </Suspense>
                </ErrorBoundary>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/asset-scout"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <ErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <AssetScoutPage />
                  </Suspense>
                </ErrorBoundary>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        {/* AI & Litigation Extended Routes */}
        <Route
          path="/ambient-billing"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <ErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <AmbientBillingPage />
                  </Suspense>
                </ErrorBoundary>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/moot-court"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <ErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <MootCourtPage />
                  </Suspense>
                </ErrorBoundary>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/deep-theory"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <ErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <DeepTheoryPage />
                  </Suspense>
                </ErrorBoundary>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        {/* M&A / Deal Routes */}
        <Route
          path="/deal-room"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <ErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <DealRoomPage />
                  </Suspense>
                </ErrorBoundary>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/deal-benchmark"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <ErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <DealBenchmarkPage />
                  </Suspense>
                </ErrorBoundary>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/deal-scanner"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <ErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <DealScannerPage />
                  </Suspense>
                </ErrorBoundary>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/synergy-scout"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <ErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <SynergyScoutPage />
                  </Suspense>
                </ErrorBoundary>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        {/* Document & Contract Routes */}
        <Route
          path="/doc-assembly"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <ErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <DocumentAssemblyPage />
                  </Suspense>
                </ErrorBoundary>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/negotiation-timeline"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <ErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <NegotiationTimelinePage />
                  </Suspense>
                </ErrorBoundary>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/model-docs"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <ErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <ModelDocumentAnalyzerPage />
                  </Suspense>
                </ErrorBoundary>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        {/* Corporate Routes */}
        <Route
          path="/corporate-mirror"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <ErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <CorporateMirrorPage />
                  </Suspense>
                </ErrorBoundary>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/fort-knox"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <ErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <FortKnoxAuditPage />
                  </Suspense>
                </ErrorBoundary>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </div>
  );
}
